import '../Sass/App.scss';
import React, { useContext, useState } from 'react';
import { Instruction } from './Instruction';
import { EditInstruction } from './EditInstruction';
import Dialog from '@mui/material/Dialog';
import { EditGameInfo } from './EditGameInfo';
import { apiFetch } from '../Helpers/api';
import { AuthContext } from '../Contexts/AuthContext';

let DEFAULT_NAME = "brief title";
let DEFAULT_DESCRIPTION = "a more complete description of the unique mechanic, rule or special card";

function gameAssetUrl(path) {
    if (!path) {
        return null;
    }

    if (/^(https?:\/\/|data:image\/)/i.test(path)) {
        return path;
    }

    if (path.startsWith('/game_assets/')) {
        return 'https://www.malwaregame.com' + path;
    }

    return 'https://www.malwaregame.com/game_assets/' + path.replace(/^\/+/, '');
}


export function CodebaseHeader(props) {

    const auth = useContext(AuthContext)
    const [gameData, setGameData] = useState(props.gameData)
    const [instructions, setInstructions] = useState(props.instructions.map((instruction, index) => { return { data: instruction, position: index}; } ))
    const [editInstructionOpen, setEditInstructionOpen] = useState(false)
    const [editInfoOpen, setEditInfoOpen] = useState(false)
    const [playtestersOpen, setPlaytestersOpen] = useState(false)
    const [playtesters, setPlaytesters] = useState([])
    const [playtesterIdentifier, setPlaytesterIdentifier] = useState('')
    const [playtestersLoading, setPlaytestersLoading] = useState(false)
    const [playtestersError, setPlaytestersError] = useState('')
    const [feedbackOpen, setFeedbackOpen] = useState(false)
    const [feedbackSubject, setFeedbackSubject] = useState('Publication review feedback')
    const [feedbackBody, setFeedbackBody] = useState('')
    const [feedbackStatus, setFeedbackStatus] = useState('Returned')
    const [feedbackWorking, setFeedbackWorking] = useState(false)
    const [feedbackError, setFeedbackError] = useState('')
    const [feedbackSuccess, setFeedbackSuccess] = useState('')
    const [currentInstruction, setCurrentInstruction] = useState()
    const [currentFeaturedImage, setCurrentFeaturedImage] = useState(gameAssetUrl(props.gameData.featured_image))
    const [currentDefaultBackImage, setCurrentDefaultBackImage] = useState(gameAssetUrl(props.gameData.default_back_image))

    const currentStatus = gameData.status || 'Draft';
    const publishableStatuses = ['Draft', 'Returned'];
    const isAdmin = auth.user?.role === 'admin';

    function saveInstruction(data) {
        setEditInstructionOpen(false);
        apiFetch("editinstruction.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ instruction_id: data.data.instruction_id, instruction_name: data.data.name, instruction_description: data.data.description }),
        })
        .then((res) => res.json())
        .then((json) => {
            const currentSet = [...instructions]
            const newInstructions = [
                ...currentSet.slice(0,data.position),
                data,
                ...currentSet.slice(data.position+1, currentSet.length)
            ];
            setInstructions(newInstructions)
        })
    }

    function deleteInstruction(data) {
        setEditInstructionOpen(false);
        apiFetch("deleteinstruction.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ instruction_id: data.data.instruction_id }),
        })
        .then((res) => res.json())
        .then((json) => {
            const currentSet = [...instructions]
            const newInstructions = [
                ...currentSet.slice(0,data.position),
                ...currentSet.slice(data.position+1, currentSet.length)
            ];
            setInstructions(newInstructions)
        })
    }

    function saveInfo(data) {
        setEditInfoOpen(false);
        const newGameData = {
            ...gameData,
            name: data.name,
            description: data.description,
            flavor: data.flavor,
            featured_image: data.featured_image,
            default_back_image: data.default_back_image,
            status: gameData.status || 'Draft'
        };
        apiFetch("editcodebase.php", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(newGameData),
        })
        .then((res) => res.json())
        .then((json) => {
            setGameData(json.data?.codebase || newGameData);
            if (json.data?.codebase?.featured_image) {
                setCurrentFeaturedImage(gameAssetUrl(json.data.codebase.featured_image));
            }
            if (json.data?.codebase?.default_back_image) {
                setCurrentDefaultBackImage(gameAssetUrl(json.data.codebase.default_back_image));
            }

        });
    }

    function publishCodebase() {
        const newGameData = {
            ...gameData,
            status: 'Under Review'
        };

        apiFetch("editcodebase.php", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(newGameData),
        })
        .then((res) => res.json())
        .then((json) => {
            setGameData(json.data?.codebase || newGameData);
        });
    }

    function addInstruction() {
        setEditInstructionOpen(false);
        apiFetch("createinstruction.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: DEFAULT_NAME, description: DEFAULT_DESCRIPTION, codebase_id: props.codebaseId }),
        })
        .then((res) => res.json())
        .then((json) => {
            const newInstruction = { 
                data: {
                    instruction_id: json.instruction_id,
                    name: DEFAULT_NAME,
                    description: DEFAULT_DESCRIPTION
                },
                position: instructions.length,
            }
            setInstructions([...instructions, newInstruction])
        })
    }

    function loadPlaytesters() {
        setPlaytestersLoading(true);
        setPlaytestersError('');

        apiFetch(`codebaseplaytesters.php?codebase_id=${encodeURIComponent(props.codebaseId)}`)
            .then((res) => res.json().then((json) => ({ res, json })))
            .then(({ res, json }) => {
                if (!res.ok || json.error) {
                    throw new Error(json.error || 'Could not load playtesters.');
                }

                setPlaytesters(json.data?.playtesters || []);
            })
            .catch((error) => {
                setPlaytesters([]);
                setPlaytestersError(error.message || 'Could not load playtesters.');
            })
            .finally(() => setPlaytestersLoading(false));
    }

    function openPlaytesterManager() {
        setPlaytestersOpen(true);
        setPlaytesterIdentifier('');
        loadPlaytesters();
    }

    function addPlaytester(event) {
        event.preventDefault();
        setPlaytestersLoading(true);
        setPlaytestersError('');

        apiFetch("codebaseplaytesters.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ codebase_id: props.codebaseId, identifier: playtesterIdentifier }),
        })
        .then((res) => res.json().then((json) => ({ res, json })))
        .then(({ res, json }) => {
            if (!res.ok || json.error) {
                throw new Error(json.error || 'Could not add playtester.');
            }

            setPlaytesters(json.data?.playtesters || []);
            setPlaytesterIdentifier('');
        })
        .catch((error) => setPlaytestersError(error.message || 'Could not add playtester.'))
        .finally(() => setPlaytestersLoading(false));
    }

    function removePlaytester(playtester) {
        setPlaytestersLoading(true);
        setPlaytestersError('');

        apiFetch("codebaseplaytesters.php", {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ codebase_id: props.codebaseId, user_id: playtester.user_id }),
        })
        .then((res) => res.json().then((json) => ({ res, json })))
        .then(({ res, json }) => {
            if (!res.ok || json.error) {
                throw new Error(json.error || 'Could not remove playtester.');
            }

            setPlaytesters(json.data?.playtesters || []);
        })
        .catch((error) => setPlaytestersError(error.message || 'Could not remove playtester.'))
        .finally(() => setPlaytestersLoading(false));
    }

    function openFeedbackDialog() {
        setFeedbackOpen(true);
        setFeedbackSubject('Publication review feedback');
        setFeedbackBody('');
        setFeedbackStatus('Returned');
        setFeedbackError('');
        setFeedbackSuccess('');
    }

    function sendFeedback(event) {
        event.preventDefault();
        setFeedbackWorking(true);
        setFeedbackError('');
        setFeedbackSuccess('');

        apiFetch("usermessages.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                codebase_id: props.codebaseId,
                subject: feedbackSubject,
                body: feedbackBody,
                status: feedbackStatus
            }),
        })
        .then((res) => res.json().then((json) => ({ res, json })))
        .then(({ res, json }) => {
            if (!res.ok || json.error) {
                throw new Error(json.error || 'Could not send feedback.');
            }

            if (json.data?.codebase_status) {
                setGameData({ ...gameData, status: json.data.codebase_status });
            }

            setFeedbackSuccess('feedback sent');
            setFeedbackBody('');
        })
        .catch((error) => setFeedbackError(error.message || 'Could not send feedback.'))
        .finally(() => setFeedbackWorking(false));
    }

    function modifyInstruction(id) {
        const selectedInstruction = instructions.filter((instruction) => String(instruction.data.instruction_id) === String(id))[0];
        setCurrentInstruction(selectedInstruction);
        setEditInstructionOpen(true);
    }

    return (
        <>
            <div className="codebase-banner">
                <img className="responsive-image" src={currentFeaturedImage} alt={gameData.name} />
                { props.editing && <div className="codebase-status">
                    
                    <div className="white-code">Codebase status: <b>{currentStatus}</b></div>
                    {publishableStatuses.includes(currentStatus) &&
                        <button className="cli-button" onClick={publishCodebase}>publish</button>
                    }
                </div> }
            </div>
            <h1>
                {gameData.name}
                {props.editing && <button className='il-button' onClick={() => setEditInfoOpen(true)}>edit game info</button>}
                {props.canEdit && <button className='il-button' onClick={openPlaytesterManager}>manage playtesters</button>}
                {isAdmin && gameData.owner_user_id && <button className='il-button' onClick={openFeedbackDialog}>send review feedback</button>}
            </h1>
            <p className="green-box">{gameData.description}</p>
            <p className="documentation-text">{gameData.flavor}</p>
            <h2>instructions and clarifications{props.editing && <button onClick={addInstruction} className='il-button'>add</button>}</h2>
            <ul className="docs-instructions">
                { instructions.map((instruction) => 
                    <Instruction data={instruction.data} key={instruction.data.instruction_id} modifyHandler={modifyInstruction} editable={props.editing} />
                ) }
            </ul>
            <Dialog
                open={editInstructionOpen}
                maxWidth='xl'
                fullWidth={true}
                onClose={() => setEditInstructionOpen(false)}
                PaperProps={{sx: {bgcolor: 'black', border: 1, borderColor: 'white', padding: 3, color: 'white'}}}
                >
                    <EditInstruction instructionData={currentInstruction} changeHandler={saveInstruction} deleteHandler={deleteInstruction} setModifyOpen={setEditInstructionOpen} />
            </Dialog>
            <Dialog
                open={editInfoOpen}
                maxWidth='xl'
                fullWidth={true}
                onClose={() => setEditInfoOpen(false)}
                PaperProps={{sx: {bgcolor: 'black', border: 1, borderColor: 'white', padding: 3, color: 'white'}}}
                >
                    <EditGameInfo gameData={gameData} changeHandler={saveInfo} setModifyOpen={setEditInfoOpen} featuredImage={currentFeaturedImage} defaultBackImage={currentDefaultBackImage} />
            </Dialog>
            <Dialog
                open={playtestersOpen}
                maxWidth='sm'
                fullWidth={true}
                onClose={() => setPlaytestersOpen(false)}
                PaperProps={{sx: {bgcolor: 'black', border: 1, borderColor: 'white', padding: 3, color: 'white'}}}
                >
                    <div className="auth-panel playtester-manager">
                        <h2>manage playtesters</h2>
                        <div className="grey-code">grant documentation and playtest access for {gameData.name}</div>
                        <form onSubmit={addPlaytester} className="playtester-form">
                            <label className="auth-field">
                                <span className="green-code">username or email</span>
                                <input value={playtesterIdentifier} onChange={(event) => setPlaytesterIdentifier(event.target.value)} />
                            </label>
                            <button className="cli-button" type="submit" disabled={playtestersLoading || playtesterIdentifier.trim() === ''}>
                                {playtestersLoading ? 'working...' : 'add playtester'}
                            </button>
                        </form>
                        {playtestersError && <div className="auth-error">{playtestersError}</div>}
                        <div className="playtester-list">
                            {playtestersLoading && playtesters.length === 0 && <div className="grey-code">loading playtesters...</div>}
                            {!playtestersLoading && playtesters.length === 0 && <div className="profile-empty">no playtesters added</div>}
                            {playtesters.map((playtester) => (
                                <div className="playtester-line" key={playtester.user_id}>
                                    <span>
                                        <span className="profile-entitlement-name">{playtester.display_name || playtester.username}</span>
                                        <span className="cli-result-description">@{playtester.username}</span>
                                    </span>
                                    <button className="cli-button-red" onClick={() => removePlaytester(playtester)} disabled={playtestersLoading}>
                                        remove
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="cli-button-white" onClick={() => setPlaytestersOpen(false)}>close</button>
                    </div>
            </Dialog>
            <Dialog
                open={feedbackOpen}
                maxWidth='md'
                fullWidth={true}
                onClose={() => setFeedbackOpen(false)}
                PaperProps={{sx: {bgcolor: 'black', border: 1, borderColor: 'white', padding: 3, color: 'white'}}}
                >
                    <form className="auth-panel feedback-manager" onSubmit={sendFeedback}>
                        <h2>send review feedback</h2>
                        <div className="grey-code">message the developer for {gameData.name}</div>
                        <label className="auth-field">
                            <span className="green-code">subject</span>
                            <input value={feedbackSubject} onChange={(event) => setFeedbackSubject(event.target.value)} />
                        </label>
                        <label className="auth-field">
                            <span className="green-code">publication status</span>
                            <select className="codebase-select feedback-status-select" style={{fontSize: '1em', maxWidth: 'none', margin: 0}} value={feedbackStatus} onChange={(event) => setFeedbackStatus(event.target.value)}>
                                <option value="">leave unchanged</option>
                                <option value="Returned">returned</option>
                                <option value="Under Review">under review</option>
                                <option value="Published">published</option>
                            </select>
                        </label>
                        <label className="auth-field">
                            <span className="green-code">message</span>
                            <textarea value={feedbackBody} onChange={(event) => setFeedbackBody(event.target.value)} />
                        </label>
                        {feedbackError && <div className="auth-error">{feedbackError}</div>}
                        {feedbackSuccess && <div className="cart-checkout-message">{feedbackSuccess}</div>}
                        <button className="cli-button" type="submit" disabled={feedbackWorking || feedbackSubject.trim() === '' || feedbackBody.trim() === ''}>
                            {feedbackWorking ? 'sending...' : 'send feedback'}
                        </button>
                        <button className="cli-button-white" type="button" onClick={() => setFeedbackOpen(false)}>close</button>
                    </form>
            </Dialog>
        </>
    )
}
